(() => {
  const prefersReduced = () => {
    if (!('matchMedia' in window)) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const num = (v) => {
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  };

  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  const getIndexVar = (el) => {
    if (!el) return 0;
    const v = getComputedStyle(el).getPropertyValue('--db-i');
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const defaultDuration = (el) => {
    if (el.closest('.db-ring')) return 950;
    if (el.closest('.db-rating10')) return 850;
    if (el.closest('.db-rangeMetric')) return 650;
    return 750;
  };

  const formatValue = (el, v) => {
    const decimals = parseInt(el.getAttribute('data-db-decimals') || '0', 10);
    if (Number.isFinite(decimals) && decimals > 0) return v.toFixed(decimals);
    return String(Math.round(v));
  };

  const animateCount = (el, delayOffset = 0) => {
    if (!el) return;
    if (el.dataset.dbCountDone === '1') return;

    const target = num(el.dataset.dbCountTarget || el.getAttribute('data-db-count'));
    if (target === null) return;

    if (prefersReduced()) {
      el.textContent = formatValue(el, target);
      el.dataset.dbCountDone = '1';
      return;
    }

    const localDelay = parseInt(el.getAttribute('data-db-delay') || '0', 10);
    const delay = delayOffset + (Number.isFinite(localDelay) ? localDelay : 0);

    const localDuration = parseInt(el.getAttribute('data-db-duration') || '', 10);
    const duration = Number.isFinite(localDuration) && localDuration > 0 ? localDuration : defaultDuration(el);

    const startFrom = num(el.getAttribute('data-db-from'));
    const from = startFrom === null ? 0 : startFrom;

    // Set the starting value only when this section is actually about to animate.
    // This avoids rewriting every below-the-fold number during initial page load.
    el.textContent = formatValue(el, from);

    const startTime = performance.now() + delay;
    el.dataset.dbCountDone = 'animating';

    const step = (now) => {
      if (now < startTime) {
        requestAnimationFrame(step);
        return;
      }
      const t = Math.min(1, (now - startTime) / duration);
      const eased = easeOut(t);
      const v = from + (target - from) * eased;
      el.textContent = formatValue(el, v);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = formatValue(el, target);
        el.dataset.dbCountDone = '1';
      }
    };

    requestAnimationFrame(step);
  };

  const animateSectionCounts = (section) => {
    if (!section || section.dataset.dbAnimated === '1') return;

    const type = section.getAttribute('data-db-section') || '';

    if (type === 'overall') {
      const items = Array.from(section.querySelectorAll('.db-overall-item'));
      for (const item of items) {
        const i = getIndexVar(item);
        const baseDelay = i * 45 + 40;
        const nums = Array.from(item.querySelectorAll('[data-db-count]'));
        for (const el of nums) animateCount(el, baseDelay);
      }
      section.dataset.dbAnimated = '1';
      return;
    }

    const ratingCards = Array.from(section.querySelectorAll('.db-rating10'));
    for (const card of ratingCards) {
      const i = getIndexVar(card);
      const baseDelay = i * 45 + 40;
      const n = card.querySelector('[data-db-count]');
      if (n) animateCount(n, baseDelay);
    }

    const sexCards = Array.from(section.querySelectorAll('.db-sexCard'));
    for (const card of sexCards) {
      const i = getIndexVar(card);
      const baseDelay = i * 60 + 40;
      const nums = Array.from(card.querySelectorAll('.db-rangeMetric__num[data-db-count]'));
      for (const el of nums) animateCount(el, baseDelay);
    }

    const metricCards = Array.from(section.querySelectorAll('.db-breed-metric'));
    for (const card of metricCards) {
      const i = getIndexVar(card);
      const baseDelay = i * 45 + 40;
      const nums = Array.from(card.querySelectorAll('[data-db-count]'));
      for (const el of nums) animateCount(el, baseDelay);
    }

    const loose = Array.from(section.querySelectorAll(':scope > [data-db-count]'));
    for (const el of loose) animateCount(el, 0);

    section.dataset.dbAnimated = '1';
  };

  const setupInView = (root) => {
    const sections = Array.from(root.querySelectorAll('.db-breed-section'));
    if (!sections.length) return;

    const mark = (el) => {
      el.classList.add('db-inview');
      animateSectionCounts(el);
    };

    if (!('IntersectionObserver' in window) || prefersReduced()) {
      sections.forEach(mark);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            mark(e.target);
            io.unobserve(e.target);
          }
        }
      },
      { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
    );

    sections.forEach((s) => io.observe(s));
  };

  const run = () => {
    const articleRoot = document.querySelector('[data-db-article]');
    if (!articleRoot) return;

    articleRoot.classList.add('db-js');

    // The DB sections are rendered server-side (Liquid) for SEO.
    // This file remains as progressive enhancement only:
    //  - count-up animations
    //  - in-view entrance effects
    // Counts are initialized lazily per section to reduce initial DOM work.
    setupInView(articleRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
